import React from 'react';

import { ExclamationCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Select, Tooltip, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useFormik } from 'formik';

import { useExportShipmentHandler } from '../api/exportShipmentData';
import { LitigationAction, LitigationFormData } from '../types';
import { litigationSchema } from '../validations/litigationSchema';

type Props = {
  open: boolean;
  handleSubmit: (data: LitigationFormData) => void;
  loading: boolean;
  handleCancel: () => void;
  litigationAction: LitigationAction | null;
  shipmentId: string;
};

const MODAL_CONTENT: Record<
  LitigationAction,
  {
    title: string;
    okText: string;
    subtitle?: string;
  }
> = {
  FLAG: {
    title: 'Are you sure you want to flag this shipment ?',
    subtitle: '*You will be able to unflag it afterwards.',
    okText: 'Flag as "Litigation"',
  },
  SOLVE: {
    title:
      'Are you sure you want to flag this shipment as “Litigation solved” ?',
    okText: 'Flag as "Litigation solved"',
  },
  CANCEL: {
    title: 'Are you sure you want to cancel the litigation for this shipment?',
    okText: 'Confirm cancellation',
  },
};

const REASON_OPTIONS = [
  { value: 'productDamaged', label: 'Product damaged' },
  { value: 'stolen', label: 'Stolen' },
  { value: 'abortedDelivery', label: 'Aborted delivery' },
  { value: 'nonDeliveryClaim', label: 'Non-Delivery Claim' },
  { value: 'other', label: 'Other' },
];

export const LitigationModal = ({
  open,
  handleSubmit,
  loading,
  handleCancel,
  litigationAction,
  shipmentId,
}: Props) => {
  const isFlag = litigationAction === 'FLAG';
  const isSolve = litigationAction === 'SOLVE';
  const shouldExport = isFlag || isSolve;
  const content = litigationAction && MODAL_CONTENT[litigationAction];

  const handleExport = useExportShipmentHandler();

  const formik = useFormik<LitigationFormData>({
    initialValues: { reason: undefined, details: '', exported: false },
    validationSchema: litigationSchema(litigationAction, isFlag),
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      const statusMap = {
        FLAG: 'FLAGGED',
        SOLVE: 'SOLVED',
        CANCEL: 'CANCELED',
      } as const;

      handleSubmit({
        status: statusMap[litigationAction!],
        reason: values.reason || '',
        details: values.details && values.details.trim(),
      });

      resetForm();
    },
  });

  const handleOk = async () => {
    formik.handleSubmit();
  };

  const handleExportClick = () => {
    handleExport(shipmentId);
    formik.setFieldValue('exported', true);
  };

  const resetAndCancel = () => {
    formik.resetForm();
    handleCancel();
  };

  return (
    <Modal
      width="48rem"
      title={content?.title}
      okText={content?.okText}
      cancelText="Cancel"
      open={open}
      onOk={handleOk}
      confirmLoading={loading}
      onCancel={resetAndCancel}
      okButtonProps={{
        danger: true,
        type: 'default',
        disabled: formik.isSubmitting,
      }}
      styles={{
        content: { padding: '1.5rem' },
        header: { marginBottom: 0 },
        footer: { marginTop: '1.5rem' },
      }}
    >
      {isFlag && (
        <Typography.Text style={{ color: '#CECECEE0' }}>
          {content?.subtitle}
        </Typography.Text>
      )}

      <Flex vertical gap="1.5rem" style={{ margin: '1.5rem 0' }}>
        {isFlag && (
          <Flex vertical>
            <Select
              data-testid="litigation-reason"
              placeholder="The reason"
              style={{ width: '100%' }}
              options={REASON_OPTIONS}
              value={formik.values.reason}
              onChange={(value) => formik.setFieldValue('reason', value)}
              onBlur={() => formik.setFieldTouched('reason', true)}
              status={
                formik.touched.reason && formik.errors.reason ? 'error' : ''
              }
            />
            {formik.touched.reason && formik.errors.reason && (
              <Typography.Text
                data-testid="reason-error-message"
                type="danger"
                style={{ fontSize: '0.75rem' }}
              >
                {formik.errors.reason}
              </Typography.Text>
            )}
          </Flex>
        )}

        <Flex vertical gap=".75rem">
          <Typography>Please give us some comments.</Typography>
          <TextArea
            data-testid="litigation-details"
            rows={4}
            name="details"
            value={formik.values.details}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            status={
              formik.touched.details && formik.errors.details ? 'error' : ''
            }
          />
          {formik.touched.details && formik.errors.details && (
            <Typography.Text
              data-testid="details-error-message"
              type="danger"
              style={{ fontSize: '0.75rem' }}
            >
              {formik.errors.details}
            </Typography.Text>
          )}
        </Flex>

        {shouldExport && isSolve ? (
          <Button
            style={{ width: 'fit-content' }}
            onClick={handleExportClick}
            icon={<UploadOutlined />}
          >
            Export this shipment
          </Button>
        ) : (
          shouldExport && (
            <Flex
              align="center"
              justify="space-between"
              style={{
                padding: '0.75rem',
                backgroundColor: '#FFEBEB',
                width: 'fit-content',
                gap: '1rem',
                borderRadius: '.75rem',
              }}
            >
              <Tooltip title="Please export the shipment data before flagging the shipment as litigation">
                <ExclamationCircleOutlined
                  style={{ fontSize: '1.2rem', color: 'red' }}
                />
              </Tooltip>

              <Button
                onClick={handleExportClick}
                color="danger"
                variant="outlined"
                icon={<UploadOutlined />}
              >
                Export this shipment
              </Button>
            </Flex>
          )
        )}

        {formik.touched.exported && formik.errors.exported && (
          <Typography.Text
            data-testid="export-data-error-message"
            type="danger"
            style={{ fontSize: '0.75rem' }}
          >
            {formik.errors.exported}
          </Typography.Text>
        )}

        <input
          type="checkbox"
          name="exported"
          checked={formik.values.exported}
          onChange={formik.handleChange}
          style={{ display: 'none' }}
        />
      </Flex>
    </Modal>
  );
};
